import React, { useEffect, useRef, useState } from 'react'
import type { CalendarEvent } from 'paintscout'
import type { Deal, User, ContactQuery, ContactQueryVariables } from '@paintscout/api'
import { ContactDocument } from '@paintscout/api'
import { useApolloClient } from '@apollo/react-hooks'
import type { DialogProps } from '@paintscout/ui'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputField,
  Typography,
  ConfirmationDialog,
  Tooltip,
  Card,
  Popper,
  makeStyles
} from '@paintscout/ui'
import {
  DealSelect,
  DropdownSelect,
  Editor,
  SendEventConfirmationDialog,
  useClientOptions,
  useCrmDialogs,
  useUser,
  useUserPermissions
} from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import CloseIcon from '@material-ui/icons/Close'
import moment from 'moment-timezone'
import { formatName, getEventTypes, getTimezone, stringifyObjectValues, uuid } from '@paintscout/util/builder'
import { useAddressSearch } from 'shared/hooks'
import { useCancelEventFlow } from 'src/hooks'
import { isLooselyEqual } from '@paintscout/util'
import classnames from 'classnames'
import AddIcon from '@material-ui/icons/Add'
import { jsonToLucene } from 'json-to-lucene'
import { goToDeal } from 'src/util/routing'
import DealDialog from '../DealDialog'
import ScheduleEventDialog from './ScheduleEventDialog'

export function getDefaultSalesDealQuery() {
  return jsonToLucene({
    filters: [
      {
        type: 'equal',
        id: 'archived',
        value: true,
        not: true
      },
      {
        type: 'equal',
        id: 'pipeline.value',
        value: 'production-pipeline',
        not: true
      }
    ]
  })
}

// a bit weird to use query strings instead of filters but crm context is build on queries so we are matching suit
export function buildDealQuery(query?: string) {
  const defaultQuery = getDefaultSalesDealQuery()
  if (!query) return defaultQuery
  return `(${defaultQuery}) AND (${query})`
}

type AddressGeocode = { lat: number; lng: number }
type AddressSource = {
  address?: string
  city?: string
  postal?: string
  province?: string
  geocode?: AddressGeocode | null
}

/**
 * Resolve the event address/geocode from a deal's job address, falling back to the deal's
 * contact address (and geocode) when the deal itself has no job address. Returns an empty
 * address / null geocode when neither has an address.
 */
export function resolveEventAddressAndGeocode({
  jobAddress,
  dealGeocode,
  contact
}: {
  jobAddress?: AddressSource | null
  dealGeocode?: AddressGeocode | null
  contact?: AddressSource | null
}): { address: string; geocode: AddressGeocode | null } {
  const fields = ['address', 'city', 'postal', 'province']
  if (jobAddress?.address) {
    return {
      address: stringifyObjectValues({ obj: jobAddress, fields }),
      geocode: dealGeocode ? { lat: dealGeocode.lat, lng: dealGeocode.lng } : null
    }
  }
  if (contact?.address) {
    return {
      address: stringifyObjectValues({ obj: contact, fields }),
      geocode: contact.geocode ? { lat: contact.geocode.lat, lng: contact.geocode.lng } : null
    }
  }
  return { address: '', geocode: null }
}

export function getFullCountryLabel(country?: string) {
  if (!country) {
    return ''
  }

  if (country === 'USA' || country === 'United States') {
    return 'United States'
  }

  if (country === 'Canada') {
    return 'Canada'
  }

  return country
}

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    paddingBottom: theme.spacing(3)
  },
  inlineButton: {
    height: theme.typography.pxToRem(41),
    fontSize: theme.typography.pxToRem(14),
    marginTop: theme.spacing(2.5),
    [theme.breakpoints.down('sm')]: {
      height: theme.typography.pxToRem(39)
    }
  },
  closeButton: {
    color: theme.palette.text.primary
  },
  inputAdornment: {
    marginRight: theme.spacing(1),
    color: theme.paintscout.palette.brand.Neutral.P85
  },
  // Anchor for the portaled address Popper. Kept positioned for the ref math
  // and as a defensive stacking-context floor.
  addressFieldContainer: {
    position: 'relative',
    zIndex: 3
  },
  // The Popper portals the dropdown to document.body to escape the Dialog's
  // stacking context entirely — required because on iPad Safari the TipTap
  // sticky toolbar (#menu-bar, zIndex: 2) was painting above siblings.
  addressPopper: {
    zIndex: 2147483005
  },
  addressDropdown: {
    boxShadow: theme.shadows[2],
    // Forces a GPU compositor layer on iOS so the dropdown is guaranteed to
    // paint above any sticky/transformed ancestor.
    transform: 'translateZ(0)'
  },
  // Caps the Editor's sticky toolbar inside its own stacking context so it
  // cannot bleed above sibling content (defense in depth).
  editorContainer: {
    position: 'relative',
    zIndex: 1,
    isolation: 'isolate'
  },
  addressItem: {
    padding: theme.spacing(1, 2),
    '&:hover': {
      outline: '1px solid #e0e0e0',
      backgroundColor: theme.palette.action.hover
    }
  },
  scheduledTime: {
    height: '39px',
    '& > input': {
      color: theme.palette.text.primary
    }
  },
  estimatorAdornment: {
    color: theme.palette.text.primary,
    marginRight: '3px'
  },
  deleteButton: {
    color: `${theme.palette.text.secondary} !important`,
    '&, & *': {
      color: `${theme.palette.text.secondary} !important`
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: `${theme.palette.error.main} !important`,
        '& *': {
          color: `${theme.palette.error.main} !important`
        },
        '& .MuiButton-label': {
          color: `${theme.palette.error.main} !important`,
          '&, & *': {
            color: `${theme.palette.error.main} !important`
          }
        }
      }
    },
    '&:active': {
      color: `${theme.palette.error.main} !important`,
      '& *': {
        color: `${theme.palette.error.main} !important`
      }
    },
    '&:focus': {
      color: `${theme.palette.error.main} !important`,
      '& *': {
        color: `${theme.palette.error.main} !important`
      }
    }
  },
  buttonErrorText: {
    marginTop: '2px',
    color: theme.palette.error.main
  },
  scheduleButton: {
    marginTop: 0
  },
  scheduledTooltip: {
    width: '100%',
    '& > div': {
      width: '100%'
    }
  },
  leftAction: {
    minWidth: 0,
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      marginLeft: `-${theme.spacing(0.25)}px`
    }
  },
  overflowVisible: {
    overflow: 'visible',
    [theme.breakpoints.down('sm')]: {
      overflow: 'auto'
    },
    '@media (max-height:750px)': {
      overflow: 'auto'
    }
  }
}))

export interface EventDialogProps extends DialogProps {
  event?: Partial<CalendarEvent>
  defaultEstimator?: string
  dealQuery?: string
  searchCompanyId?: string
  date?: Date
  selectedUsers?: User[]
  onConfirm: (event: CalendarEvent, eventTemplate?: string) => void
  onDelete?: (event: CalendarEvent, eventTemplate?: string) => void
  isNew?: boolean
  onCancel?: () => void
}

interface DialogLeftButtonProps {
  event: Partial<CalendarEvent>
  isNew?: boolean
  onDelete?: (event: CalendarEvent, eventTemplate?: string) => void
  onCancel?: () => void
  onCancelEvent: () => void
  cancelButtonClassName: string
  leftActionClassName: string
}

function DialogLeftButton({
  event,
  isNew,
  onDelete,
  onCancel,
  onCancelEvent,
  cancelButtonClassName,
  leftActionClassName
}: DialogLeftButtonProps) {
  const hasStart = !!event.start
  // Only events whose start is still in the future can be canceled (including later today);
  // events that have already started/passed cannot.
  const isFuture = event.start && moment(event.start).isAfter(moment())
  if (!isNew && !!onDelete && hasStart && isFuture) {
    return (
      <Button
        variant="textNoPadding"
        onClick={onCancelEvent}
        className={classnames(cancelButtonClassName, leftActionClassName)}
      >
        Cancel
      </Button>
    )
  }
  if (onCancel) {
    return (
      <Button variant="textNoPadding" onClick={onCancel} className={leftActionClassName}>
        Back
      </Button>
    )
  }
  return null
}

/**
 * For Sales aka Non-Production Events
 */
export default function EventDialog(props: EventDialogProps) {
  const classes = useStyles(props)
  const {
    event: propEvent,
    onConfirm,
    onCancel,
    onDelete,
    dealQuery: propDealQuery,
    isNew,
    date,
    searchCompanyId = null,
    selectedUsers,
    ...otherProps
  } = props
  const { openDialog, dismissDialog, dismissAllDialogs, crmContext } = useCrmDialogs()
  const { subtitle, deal: contextDeal } = crmContext || {}
  const { hide: hideDeal, search } = contextDeal || {}
  const { query: contextDealQuery } = search || {}

  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const { user } = useUser()
  const { permissionToViewAllEvents } = useUserPermissions()

  const apolloClient = useApolloClient()

  // Resolve the event address/geocode from a deal. The deal's embedded contact has no address
  // fields, so when the deal has no job address we fetch the full contact to fall back to its
  // address (and geocode).
  const resolveAddressAndGeocode = async (deal: Partial<Deal>) => {
    const jobAddress = deal.jobAddress as AddressSource | undefined
    let contact: AddressSource | null = null
    if (!jobAddress?.address && deal.contact?.id) {
      try {
        const { data } = await apolloClient.query<ContactQuery, ContactQueryVariables>({
          query: ContactDocument,
          variables: { id: deal.contact.id },
          fetchPolicy: 'cache-first'
        })
        const fetched = data?.contact
        contact = fetched
          ? {
              address: fetched.address,
              city: fetched.city,
              postal: fetched.postal,
              province: fetched.province,
              geocode: fetched.geocode
            }
          : null
      } catch (err) {
        // A failed contact fetch shouldn't leave the dialog stuck — fall back to the
        // deal-only (empty) address so the user can still fill it in manually.
        console.error(err)
      }
    }
    return resolveEventAddressAndGeocode({ jobAddress, dealGeocode: deal.geocode, contact })
  }

  const [event, setEvent] = useState<Partial<CalendarEvent>>(propEvent || { id: uuid() })
  const [scheduleError, setScheduleError] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [addressOpen, setAddressOpen] = useState(false)
  const addressAnchorRef = useRef<HTMLDivElement>(null)
  const [dealOpen, setDealOpen] = useState(false)

  const [dealQuery, setDealQuery] = useState(buildDealQuery(propDealQuery || contextDealQuery))

  useEffect(() => {
    setDealQuery(buildDealQuery(propDealQuery || contextDealQuery))
  }, [propDealQuery, contextDealQuery])

  const handleConfirm = (e) => {
    e.preventDefault()
    setSubmitted(true)
    if (!event.estimator || !event.start) {
      setScheduleError(true)
      return
    }
    if (!event.eventType || (!hideDeal && !event.deal?.id)) {
      return
    }
    const startDateIsEqual = event.start === propEvent?.start
    const endDateIsEqual = event.end === propEvent?.end
    if (isLooselyEqual(event, propEvent) && startDateIsEqual && endDateIsEqual) {
      dismissDialog()
      return
    }
    const dateTimeChange = event.start !== propEvent?.start
    const locationChange = event.address !== propEvent?.address
    const estimatorChange = event.estimator.id !== propEvent?.estimator?.id
    if (isNew) {
      openDialog(SendEventConfirmationDialog, {
        title: 'Send Event',
        message: (
          <Typography variant={'body1'}>
            Would you like to send an event confirmation email to <strong>{formatName(event.contact)}</strong>?
          </Typography>
        ),
        missingEmailMessage: !event.contact?.email ? (
          <Typography variant="body1" align="left">
            There is no <strong>email</strong> associated to <strong>{formatName(event.contact)}</strong>, which is
            required to send the event confirmation email.
          </Typography>
        ) : null,
        onConfirm: (send: boolean, email?: string) => {
          const updatedEvent =
            email && !event.contact?.email ? { ...event, contact: { ...event.contact, email } } : event
          setEvent(updatedEvent)
          onConfirm(updatedEvent as CalendarEvent, send ? 'event-confirmation' : null)
          dismissAllDialogs()
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    } else if (dateTimeChange || locationChange || estimatorChange) {
      const newContact = propEvent?.contact?.id !== event.contact?.id
      openDialog(SendEventConfirmationDialog, {
        title: 'Send Event Change',
        message: newContact ? (
          <Typography variant={'body1'}>Would you like to notify guests of the event change?</Typography>
        ) : (
          <Typography variant={'body1'}>
            Would you like to notify <strong>{formatName(event.contact)}</strong> of the event change?
          </Typography>
        ),
        missingEmailMessage: !event.contact?.email ? (
          <Typography variant="body1" align="center">
            There is no <strong>email</strong> associated to <strong>{formatName(event.contact)}</strong>, which is
            required to send the event changed email.
          </Typography>
        ) : null,
        onConfirm: (send: boolean, email?: string) => {
          const updatedEvent =
            email && !event.contact?.email ? { ...event, contact: { ...event.contact, email } } : event
          setEvent(updatedEvent)
          onConfirm(updatedEvent as CalendarEvent, send ? 'event-rescheduled' : null)
          dismissAllDialogs()
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    } else {
      onConfirm(event as CalendarEvent, null)
      dismissDialog()
    }
  }

  const cancelEventFlow = useCancelEventFlow()
  const handleCancelEvent = () => cancelEventFlow(event as CalendarEvent, (evt, template) => onDelete?.(evt, template))

  const handleEditDeal = () => {
    const tempDeal = event?.deal || null
    openDialog(DealDialog, {
      dealId: event.deal?.id && !dealOpen ? event.deal?.id : null,
      hideProductionPipeline: true,
      onConfirm: async (deal: Deal) => {
        const matchingContact = deal.contact?.id === event.contact?.id
        const { address, geocode } = await resolveAddressAndGeocode(deal)
        setEvent((prev) => ({
          ...prev,
          deal: { id: deal._id, source: deal.lead_source, name: deal.name || '' },
          contact: deal.contact
            ? {
                id: deal.contact.id,
                firstName: deal.contact.firstName || (matchingContact ? event.contact?.firstName : ''),
                lastName: deal.contact.lastName || (matchingContact ? event.contact?.lastName : ''),
                phoneNumber: deal.contact.phoneNumber || (matchingContact ? event.contact?.phoneNumber : ''),
                email: deal.contact.email || (matchingContact ? event.contact?.email : ''),
                preferredContactMethod:
                  deal.contact.preferredContactMethod || (matchingContact ? event.contact?.preferredContactMethod : ''),
                companyId: deal.contact.companyId || (matchingContact ? event.contact?.companyId : '')
              }
            : null,
          geocode,
          address
        }))
        dismissDialog()
      },
      onBack: function () {
        setEvent((prev) => ({
          ...prev,
          deal: tempDeal
        }))
        dismissDialog()
      },
      onCancel: handleClose
    })
  }

  useEffect(() => {
    if (!permissionToViewAllEvents && !event.estimator?.id) {
      setEvent((prev) => ({
        ...prev,
        estimator: {
          id: user.user_id,
          name: formatName(user.user_metadata),
          email: user.email,
          color: user.user_metadata?.color
        }
      }))
    }
  }, [permissionToViewAllEvents])

  const { addressSearchResults, debouncedAddressSearch } = useAddressSearch({
    options: options as any
  })

  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setAddressOpen(true)
    debouncedAddressSearch(value)
    setEvent((prev) => ({
      ...prev,
      address: value
    }))
  }

  const eventTypeOptions = getEventTypes({ options }).map((eventType) => ({
    label: eventType.label,
    value: eventType.key
  }))

  if (event.eventType && !eventTypeOptions.some((option) => option.value === event.eventType)) {
    const deletedEventType = getEventTypes({ options, includeDeleted: true }).find((et) => et.key === event.eventType)
    if (deletedEventType) {
      eventTypeOptions.push({
        label: deletedEventType.label,
        value: deletedEventType.key
      })
    }
  }

  const handleDealChange = async (dealOption: DropdownSelectOption & Deal) => {
    const matchingContact = dealOption.contact?.id === event.contact?.id

    const { address, geocode } = await resolveAddressAndGeocode(dealOption)

    setEvent((prev) => ({
      ...prev,
      deal: { id: dealOption.value, source: dealOption.lead_source || prev.deal?.source, name: dealOption.name || '' },
      address,
      geocode,
      contact: dealOption.contact
        ? {
            id: dealOption.contact.id,
            firstName: dealOption.contact.firstName || (matchingContact ? event.contact?.firstName : ''),
            lastName: dealOption.contact.lastName || (matchingContact ? event.contact?.lastName : ''),
            phoneNumber: dealOption.contact.phoneNumber || (matchingContact ? event.contact?.phoneNumber : ''),
            email: dealOption.contact.email || (matchingContact ? event.contact?.email : ''),
            preferredContactMethod: matchingContact
              ? dealOption.contact.preferredContactMethod || event.contact?.preferredContactMethod
              : '',
            companyId: dealOption.contact.companyId || (matchingContact ? event.contact?.companyId : '')
          }
        : null
    }))
  }

  const getScheduledTime = () => {
    if (!event?.start || !event?.end) {
      return '-'
    }
    const startTime = moment(event.start).tz(timezone).format('h:mma')
    const endTime = moment(event.end).tz(timezone).format('h:mma')
    const date = moment(event.start).tz(timezone).format('MMMM D, YYYY')
    return `${startTime} - ${endTime} — ${date}`
  }

  const handleSchedule = () => {
    openDialog(ScheduleEventDialog, {
      event,
      date,
      selectedUsers,
      isNew,
      onConfirm: (scheduledEvent) => {
        setEvent((prev) => ({ ...prev, ...scheduledEvent }))
        setScheduleError(false)
        dismissDialog()
      },
      onClose: handleClose
    })
  }

  const handleClose = () => {
    if (isLooselyEqual(event, propEvent, ['id'])) {
      dismissAllDialogs()
    } else {
      openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. If you exit now, your changes will be lost.',
        onConfirm: () => {
          dismissAllDialogs()
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    }
  }

  return (
    <Dialog open {...otherProps} classes={{ paper: classes.overflowVisible }}>
      <DialogTitle
        subtitle={subtitle}
        rightContent={
          <IconButton className={classes.closeButton} size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        }
      >
        {isNew ? 'Add Event' : 'Event Details'}
      </DialogTitle>
      <DialogContent className={classnames(classes.dialogContent, classes.overflowVisible)}>
        <form id="event-dialog-form" autoComplete="off" onSubmit={handleConfirm}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <DropdownSelect
                className="event-dialog-event-type-select"
                variant="single"
                label={'Event Type'}
                options={eventTypeOptions}
                value={eventTypeOptions.find((option) => option.value === event.eventType)}
                onChange={(option) => {
                  setEvent((prev) => ({ ...prev, eventType: option.value }))
                }}
                required
                error={submitted && !event.eventType}
                showErrorBeforeValidation={submitted && !event.eventType}
              />
            </Grid>
            {!hideDeal && (
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs>
                    <DealSelect
                      className="event-dialog-deal-select"
                      disabled={!isNew}
                      hideIcon={!isNew}
                      query={dealQuery}
                      searchCompanyId={searchCompanyId}
                      label="Deal"
                      fullWidth
                      variant="single"
                      searchable
                      searchByName
                      nameValue={event?.deal?.name || ''}
                      value={event?.deal?.id ?? null}
                      onChange={(dealOption: DropdownSelectOption) => {
                        handleDealChange(dealOption as DropdownSelectOption & Deal)
                      }}
                      required
                      error={submitted && !event.deal?.id}
                      showErrorBeforeValidation={submitted && !event.deal?.id}
                      popperModifiers={{
                        flip: {
                          enabled: false
                        },
                        preventOverflow: {
                          enabled: false
                        }
                      }}
                      onFocus={() => {
                        setDealOpen(true)
                      }}
                      onBlur={() => {
                        setDealOpen(false)
                      }}
                    />
                  </Grid>
                  <Grid item>
                    {isNew ? (
                      <Button
                        fullWidth
                        variant="dialog"
                        className={classnames(classes.inlineButton)}
                        onMouseDown={handleEditDeal}
                        onTouchStart={handleEditDeal}
                        startIcon={!event.deal?.id ? <AddIcon /> : null}
                      >
                        {event.deal?.id && !dealOpen ? 'Edit' : 'New'}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="dialog"
                        className={classnames(classes.inlineButton)}
                        onMouseDown={() => goToDeal(event.deal?.id)}
                        onTouchStart={() => goToDeal(event.deal?.id)}
                        startIcon={null}
                      >
                        View Deal
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={12} md={6} className={classes.addressFieldContainer} ref={addressAnchorRef}>
              <InputField
                autoComplete={'off'}
                inputProps={{
                  role: 'presentation'
                }}
                id="address"
                label={'Location'}
                placeholder={'Enter address'}
                fullWidth={true}
                value={event.address || ''}
                onChange={handleAddressChange}
                onClick={() => setAddressOpen(true)}
                onBlur={() => setAddressOpen(false)}
              />
              <Popper
                open={addressSearchResults.length > 0 && addressOpen}
                anchorEl={addressAnchorRef.current}
                placement="bottom-start"
                className={classes.addressPopper}
                style={{ width: addressAnchorRef.current?.offsetWidth }}
                modifiers={{
                  flip: { enabled: false },
                  preventOverflow: { enabled: false }
                }}
              >
                <Card className={classes.addressDropdown}>
                  {addressSearchResults.map((result, i) => {
                    const label = [result.address, result.municipality].filter((item) => item).join(', ')
                    const fullCountry = getFullCountryLabel(result.country)
                    const label2 = [result.region, result.postalCode, fullCountry].filter((item) => item).join(', ')

                    return (
                      <div
                        key={i}
                        onMouseDown={() => {
                          setAddressOpen(false)
                          setEvent((prev) => ({
                            ...prev,
                            address: label
                          }))
                        }}
                        className={classes.addressItem}
                      >
                        <Typography variant={'body1'}>{label}</Typography>
                        <Typography variant={'subtitle2'}>{label2}</Typography>
                      </div>
                    )
                  })}
                </Card>
              </Popper>
            </Grid>

            <Grid item xs={12} className={classnames('event-dialog-description', classes.editorContainer)}>
              <Editor
                label={'Description'}
                content={event.note}
                onChange={(value) => {
                  setEvent((prev) => ({ ...prev, note: value }))
                }}
              />
            </Grid>
            {isNew && !(event.start && event.estimator?.id) && (
              <Grid item>
                <Button
                  onClick={handleSchedule}
                  className={classnames(classes.inlineButton, classes.scheduleButton)}
                  fullWidth
                  variant="dialog"
                  error={scheduleError}
                >
                  Schedule User
                </Button>
                {scheduleError && (
                  <Typography component={'p'} variant="caption" className={classes.buttonErrorText}>
                    This action is required.
                  </Typography>
                )}
              </Grid>
            )}
            {event.start && event.estimator?.id && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs>
                    <Tooltip hideIcon content={getScheduledTime()} classes={{ badgeRoot: classes.scheduledTooltip }}>
                      <InputField
                        fullWidth
                        label={'Scheduled User'}
                        value={getScheduledTime()}
                        disabled
                        classes={{ root: classes.scheduledTime, adornment: classes.estimatorAdornment }}
                        startAdornment={
                          event.estimator?.name ? <strong>{truncateName(event.estimator?.name)}</strong> : null
                        }
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Button
                      onClick={handleSchedule}
                      className={classnames(classes.inlineButton)}
                      fullWidth
                      variant="dialog"
                    >
                      Edit
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions
        alignLeftButtonWithTitle={false}
        leftButton={
          <DialogLeftButton
            event={event}
            isNew={isNew}
            onDelete={onDelete}
            onCancel={onCancel}
            onCancelEvent={handleCancelEvent}
            cancelButtonClassName={classes.deleteButton}
            leftActionClassName={classes.leftAction}
          />
        }
      >
        <Button
          type="submit"
          form="event-dialog-form"
          onClick={() => {
            if (!event.estimator || !event.start) {
              setScheduleError(true)
            }
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function truncateName(name: string) {
  if (!name) {
    return ''
  }
  if (name.length <= 30) {
    return name
  }
  return name.length > 30 ? `${name.slice(0, 17)}...` : name
}
